import { joinCn } from '../util';
import { VCircleProgressBarSize, VCircleProgressBarVariant } from './constant';
import type { VCircleProgressBarProps } from './type';

const style = {
  vProgressBar: joinCn(),
  vProgressBarContainer: joinCn('flex w-full cursor-pointer'),
  vProgressBarBackground: joinCn(
    'relative',
    'flex-1',
    'h-full',
    'bg-gray-300',
    'rounded-full',
    'overflow-x-hidden',
  ),
  vProgressBarValue: joinCn(
    'absolute top-0 left-0',
    'bg-secondary-300',
    'h-full w-full',
    'rounded-full',
    'translate-x-[calc(var(--value)-100%)]',
    'transition-all',
    'duration-(--duration)',
    'ease-(--timingFunction)',
  ),

  vCircleProgressBar: joinCn(
    'relative',
    'size-(--width)',
    'bg-white',
    'rounded-full',
  ),

  vCircleProgressBarAfter: (variant: VCircleProgressBarProps['variant']) => {
    const border =
      variant === VCircleProgressBarVariant.COMPLETED
        ? joinCn('border-status-completed')
        : variant === VCircleProgressBarVariant.ERROR_DANGER
          ? joinCn('border-status-failed')
          : joinCn('border-status-inProgress');

    return joinCn(
      'absolute top-0 right-0',
      'h-full w-[calc(var(--width)/2)]',
      'block',
      'bg-transparent',
      'border-[calc(var(--borderWidth))]',
      'border-l-0',
      'rounded-tr-(--width) rounded-br-(--width)',
      'rotate-z-(--degAfter)',
      'origin-left',
      'z-1',
      border,
    );
  },

  vCircleProgressBarBefore: (variant: VCircleProgressBarProps['variant']) => {
    const border =
      variant === VCircleProgressBarVariant.COMPLETED
        ? joinCn('border-status-completed')
        : variant === VCircleProgressBarVariant.ERROR_DANGER
          ? joinCn('border-status-failed')
          : joinCn('border-status-inProgress');

    return joinCn(
      'absolute top-0 left-0',
      'h-full w-[calc(var(--width)/2)]',
      'block',
      'bg-transparent',
      'border-[calc(var(--borderWidth))]',
      'border-r-0',
      'rounded-tl-(--width) rounded-bl-(--width)',
      'rotate-z-(--degBefore)',
      'origin-right',
      'z-3',
      border,
    );
  },

  vCircleProgressBarBackground: joinCn(
    'relative',
    'bg-transparent',
    'h-full w-full',
    'border-gray-300',
    'rounded-full',
    `border-[calc(var(--borderWidth))]`,
  ),

  vCircleProgressBarValueText({
    size,
    variant,
  }: Pick<VCircleProgressBarProps, 'size' | 'variant'>) {
    const textColor =
      variant === VCircleProgressBarVariant.COMPLETED
        ? joinCn('text-status-completed')
        : variant === VCircleProgressBarVariant.ERROR_DANGER
          ? joinCn('text-status-failed')
          : joinCn('text-status-inProgress');

    return joinCn(
      {},
      size === VCircleProgressBarSize.SMALL ? 'hidden' : '',
      size === VCircleProgressBarSize.MEDIUM
        ? ['text-body-l font-bold', textColor]
        : '',
    );
  },

  vCircleProgressBarOverlay: joinCn(
    'absolute top-0 left-0',
    'bg-transparent',
    'h-full w-[calc(var(--width)/2)]',
    'border-gray-300',
    'rounded-tl-(--width) rounded-bl-(--width)',
    'z-2',
    `border-t-[calc(var(--borderWidth))]`,
    `border-b-[calc(var(--borderWidth))]`,
    `border-l-[calc(var(--borderWidth))]`,
  ),
} as const;

export default style;
